/**
 * WebSocket client with auto-reconnect and feed subscriptions.
 *
 * Connects to the CNS server, handles hello/ack handshake,
 * ping/pong keepalive, and routes incoming feed messages
 * to the appropriate stores.
 *
 * Uses exponential backoff with jitter for reconnection.
 */

import { WS } from '@assistmd/config';
import type {
  ServerToClientMessage,
  HelloAckMessage,
  PongMessage,
  FeedEventMessage,
  FeedId,
} from '@assistmd/types';
import { useWsStore } from '@/lib/stores/ws-store';
import { useIntelligenceStore } from '@/lib/stores/intelligence-store';
import { useVisitStore } from '@/lib/stores/visit-store';
import { useTelemetryStore } from '@/lib/stores/telemetry-store';
import { useLcvfrStore } from '@/lib/stores/lcvfr-store';
import type { VisitId, ArtifactId, RunId, EncounterId, ArtifactType, LcvfrRunStatus } from '@assistmd/types';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface WsClientOptions {
  url: string;
  doctorId: string;
  clinicId?: string;
  subscribeFeeds?: FeedId[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Client
// ─────────────────────────────────────────────────────────────────────────────

let socket: WebSocket | null = null;
let pingTimer: ReturnType<typeof setInterval> | null = null;
let pongTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectAttempt = 0;
let clientOptions: WsClientOptions | null = null;

/**
 * Connect to the CNS WebSocket server.
 */
export function connect(options: WsClientOptions): void {
  clientOptions = options;
  reconnectAttempt = 0;
  doConnect();
}

/**
 * Disconnect from the CNS WebSocket server.
 */
export function disconnect(): void {
  cleanup();
  clientOptions = null;
  reconnectAttempt = 0;
  useWsStore.getState().setDisconnected();
}

/**
 * Send a message to the server.
 */
export function send(message: Record<string, unknown>): void {
  if (socket?.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify(message));
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal
// ─────────────────────────────────────────────────────────────────────────────

function doConnect(): void {
  if (!clientOptions) return;

  cleanup();
  useWsStore.getState().setConnecting();

  socket = new WebSocket(clientOptions.url);

  socket.onopen = () => {
    // Send hello
    send({
      type: 'hello',
      payload: {
        clientKind: 'dashboard',
        doctorId: clientOptions!.doctorId,
        clinicId: clientOptions!.clinicId,
        subscribeFeeds: clientOptions!.subscribeFeeds ?? ['A', 'C', 'D', 'E'],
        version: '2.0.0',
      },
    });

    // Start ping interval
    pingTimer = setInterval(sendPing, WS.PING_INTERVAL_MS);
  };

  socket.onmessage = (event) => {
    try {
      const msg = JSON.parse(event.data as string) as ServerToClientMessage;
      handleMessage(msg);
    } catch {
      // Ignore malformed messages
    }
  };

  socket.onclose = () => {
    cleanup();
    scheduleReconnect();
  };

  socket.onerror = () => {
    useWsStore.getState().setDisconnected('WebSocket error');
  };
}

function handleMessage(msg: ServerToClientMessage): void {
  switch (msg.type) {
    case 'hello-ack': {
      const ack = msg as HelloAckMessage;
      useWsStore.getState().setConnected(
        ack.payload.clientId,
        ack.payload.role,
        ack.payload.serverVersion,
      );
      break;
    }

    case 'pong': {
      const pong = msg as PongMessage;
      clearPongTimeout();
      const latency = Date.now() - pong.payload.clientTime;
      useWsStore.getState().setLatency(latency);
      break;
    }

    case 'feed-event': {
      const feedEvent = msg as FeedEventMessage;
      routeFeedMessage(feedEvent);
      break;
    }

    case 'transcript': {
      // Direct transcript message (not wrapped in feed-event)
      const store = useIntelligenceStore.getState();
      const payload = msg.payload as Record<string, unknown>;
      const entry = {
        id: payload.segmentId as string,
        visitId: payload.visitId as string,
        speaker: payload.speaker as number,
        role: payload.role as 'clinician' | 'patient' | 'other',
        text: payload.text as string,
        startMs: payload.startMs as number,
        endMs: payload.endMs as number,
        isFinal: payload.isFinal as boolean,
        confidence: payload.confidence as number,
      };
      if (entry.isFinal) {
        store.addTranscriptEntry(entry);
        store.setInterimEntry(null);
      } else {
        store.setInterimEntry(entry);
      }
      break;
    }

    case 'error': {
      // Server error, log only non-PHI details
      break;
    }
  }
}

function routeFeedMessage(feedEvent: FeedEventMessage): void {
  const intStore = useIntelligenceStore.getState();
  const visitStore = useVisitStore.getState();
  const telStore = useTelemetryStore.getState();
  const data = feedEvent.data as Record<string, unknown>;
  const kind = data.kind as string;

  // Track event bus metrics for telemetry
  telStore.updateEventBusMetric(`feed-${feedEvent.feed}`);

  switch (feedEvent.feed) {
    case 'A': {
      if (kind === 'transcript') {
        const entry = data.entry as {
          id: string;
          visitId: string;
          speaker: number;
          role: 'clinician' | 'patient' | 'other';
          text: string;
          startMs: number;
          endMs: number;
          isFinal: boolean;
          confidence: number;
        };
        if (entry.isFinal) {
          intStore.addTranscriptEntry(entry);
          intStore.setInterimEntry(null);
        } else {
          intStore.setInterimEntry(entry);
        }
      } else if (kind === 'record-started') {
        const recData = data as {
          visitId: string;
          transcriptId: string;
          patientCode: string;
        };
        visitStore.updateVisit(recData.visitId as VisitId, {
          isRecording: true,
          status: 'recording',
        });
      } else if (kind === 'record-stopped') {
        const recData = data as { visitId: string };
        visitStore.updateVisit(recData.visitId as VisitId, {
          isRecording: false,
        });
      }
      break;
    }

    case 'B': {
      // Feed B: Suggestions and actions (concierge)
      // Currently logged for telemetry; UI for suggestions is future work
      break;
    }

    case 'C': {
      if (kind === 'alert') {
        const alertData = data.data as {
          id: string;
          visitId?: string;
          severity: 'critical' | 'warning' | 'info';
          code: string;
          message: string;
          at: string;
        };
        intStore.addAlert(alertData);
      }
      break;
    }

    case 'D': {
      if (kind === 'visit-state-patched') {
        const patchData = data.data as {
          visitId: string;
          patch: Record<string, unknown>;
        };
        const patch: Record<string, unknown> = {};
        if (patchData.patch.status) {
          patch.status = patchData.patch.status;
        }
        if (patchData.patch.consentStatus) {
          patch.consentState = patchData.patch.consentStatus;
        }
        visitStore.updateVisit(patchData.visitId as VisitId, patch);
      } else if (kind === 'automation-job-update') {
        const jobData = data.data as {
          jobId: string;
          status: string;
          progress?: number;
          type?: string;
        };
        // Track automation job metrics for telemetry
        if (jobData.status === 'succeeded' || jobData.status === 'failed') {
          telStore.updateEventBusMetric('automation');
        }
      }
      break;
    }

    case 'E': {
      if (kind === 'soap-ready') {
        const soapData = data.data as {
          visitId: string;
          soap: { S: string; O: string; A: string; P: string };
          version: number;
        };
        intStore.setSoap({ ...soapData.soap, version: soapData.version });
      } else if (kind === 'summary-ready') {
        const summaryData = data.data as {
          visitId: string;
          short: string;
          version: number;
        };
        intStore.setSummary(summaryData.short, summaryData.version);
      } else if (kind === 'intelligence-result') {
        const resultData = data.data as {
          taskKind: string;
          visitId: string;
          status: string;
          modelId: string | null;
          generatedAt: string;
          payload: Record<string, unknown> | unknown;
        };
        // Track LLM completion in telemetry — extract actual values from payload
        if (resultData.status === 'ready' || resultData.status === 'error') {
          const payload = (resultData.payload ?? {}) as Record<string, unknown>;
          const meta = (payload.meta ?? {}) as Record<string, unknown>;
          const usage = ((payload.usage ?? meta.usage ?? {}) as Record<string, unknown>);
          const generatedAt = resultData.generatedAt ? new Date(resultData.generatedAt).getTime() : 0;
          const latencyMs = generatedAt > 0 ? Date.now() - generatedAt : ((usage.latencyMs as number | undefined) ?? 0);
          telStore.addLlmCall({
            taskId: `${resultData.visitId}-${resultData.taskKind}-${Date.now()}`,
            kind: resultData.taskKind,
            provider: (payload.provider as string | undefined) ?? 'unknown',
            modelId: resultData.modelId ?? (payload.model as string | undefined) ?? 'unknown',
            latencyMs,
            inputTokens: (usage.inputTokens as number | undefined) ?? (usage.prompt_tokens as number | undefined) ?? 0,
            outputTokens: (usage.outputTokens as number | undefined) ?? (usage.completion_tokens as number | undefined) ?? 0,
            totalTokens: (usage.totalTokens as number | undefined) ?? (usage.total_tokens as number | undefined) ?? 0,
            status: resultData.status === 'ready' ? 'completed' : 'failed',
            timestamp: Date.now(),
          });
        }
      } else if (kind === 'lcvfr-artifact') {
        // LCVFR artifact stream — route to the LCVFR store
        const lcvfrStore = useLcvfrStore.getState();
        const artifactData = data.data as {
          id: string;
          runId: string;
          encounterId: string;
          nodeId: string | null;
          type: string;
          sequence: number;
          contentText: string | null;
          contentJson: Record<string, unknown> | null;
          metaJson: Record<string, unknown> | null;
          createdAt: string;
        };
        // Bind encounter so store filters artifacts correctly
        if (artifactData.encounterId) {
          lcvfrStore.setActiveEncounter(artifactData.encounterId as EncounterId);
        }
        lcvfrStore.upsertArtifact({
          id: artifactData.id as ArtifactId,
          runId: artifactData.runId as RunId,
          encounterId: artifactData.encounterId as EncounterId,
          nodeId: artifactData.nodeId,
          type: artifactData.type as ArtifactType,
          sequence: artifactData.sequence,
          contentText: artifactData.contentText,
          contentJson: artifactData.contentJson,
          metaJson: artifactData.metaJson,
          createdAt: artifactData.createdAt,
        });
      } else if (kind === 'lcvfr-run-complete') {
        // LCVFR run completion — track in store for run console
        const lcvfrStore = useLcvfrStore.getState();
        const runData = data.data as {
          runId: string;
          status: string;
          nodeCount: number;
          artifactsWritten: number;
          totalDurationMs: number;
          error: string | null;
          finishedAt: string;
        };
        lcvfrStore.addRunSummary({
          runId: runData.runId as RunId,
          status: runData.status as LcvfrRunStatus,
          nodeCount: runData.nodeCount,
          artifactsWritten: runData.artifactsWritten,
          totalDurationMs: runData.totalDurationMs,
          error: runData.error,
          finishedAt: runData.finishedAt,
        });
      } else if (kind === 'lcvfr-pipeline-status') {
        // Pipeline active/idle notification
        const lcvfrStore = useLcvfrStore.getState();
        const statusData = data.data as { active: boolean };
        lcvfrStore.setPipelineActive(statusData.active);
      }
      break;
    }
  }
}

function sendPing(): void {
  send({
    type: 'ping',
    payload: { clientTime: Date.now() },
  });

  // Start pong timeout
  pongTimer = setTimeout(() => {
    // Pong not received in time, reconnect
    socket?.close();
  }, WS.PONG_TIMEOUT_MS);
}

function clearPongTimeout(): void {
  if (pongTimer) {
    clearTimeout(pongTimer);
    pongTimer = null;
  }
}

function cleanup(): void {
  if (pingTimer) {
    clearInterval(pingTimer);
    pingTimer = null;
  }
  clearPongTimeout();
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (socket) {
    socket.onopen = null;
    socket.onmessage = null;
    socket.onclose = null;
    socket.onerror = null;
    if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
      socket.close();
    }
    socket = null;
  }
}

function scheduleReconnect(): void {
  if (!clientOptions) return;

  reconnectAttempt++;
  useWsStore.getState().setReconnecting(reconnectAttempt);

  // Exponential backoff with jitter
  const baseDelay = Math.min(
    WS.RECONNECT_INITIAL_MS * Math.pow(WS.RECONNECT_MULTIPLIER, reconnectAttempt - 1),
    WS.RECONNECT_MAX_MS,
  );
  const jitter = baseDelay * WS.RECONNECT_JITTER * (Math.random() * 2 - 1);
  const delay = Math.max(WS.RECONNECT_INITIAL_MS, baseDelay + jitter);

  reconnectTimer = setTimeout(doConnect, delay);
}
